import { motion } from "framer-motion";
import { VISITOR_STEPS } from "@/lib/monuments";

const EASE = [0.22, 1, 0.36, 1];

export default function VisitorTransformation() {
  return (
    <section
      id="experience"
      data-testid="transformation-section"
      className="relative py-20 md:py-28 border-t border-gold/10 bg-gradient-to-b from-obsidian via-midnight to-obsidian"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-8 items-end mb-14 md:mb-16">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 font-mono-accent text-[11px] text-gold/80">
              <span>Chapter 02</span>
              <span className="w-8 h-px bg-gold/40" />
              <span className="text-bone/50">The Ritual</span>
            </div>
            <h2 className="mt-6 font-display text-5xl md:text-7xl leading-[0.95] tracking-tight text-bone">
              From <em className="text-gold not-italic">observer</em>
              <br />
              to participant.
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <p className="text-bone/70 text-lg leading-relaxed max-w-xl">
              Three steps translate a visitor from a passive spectator of ruins
              into a resident of the reconstructed world. No cables. No
              construction. Only a headset, a place, and a story that unfolds
              around you.
            </p>
          </div>
        </div>

        <div className="space-y-20 md:space-y-28">
          {VISITOR_STEPS.map((s, idx) => {
            const reverse = idx % 2 === 1;
            return (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 1.1, ease: EASE }}
                className="grid md:grid-cols-12 gap-8 md:gap-14 items-center"
                data-testid={`transformation-step-${idx + 1}`}
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.94 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 1.2, ease: EASE, delay: 0.15 }}
                  className={`md:col-span-6 relative ${reverse ? "md:order-2" : ""}`}
                >
                  <div className="relative aspect-[4/3] overflow-hidden border border-gold/15 bg-surface">
                    <img
                      src={s.image}
                      alt={s.title}
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-obsidian/70 via-transparent to-transparent" />
                    <div className="absolute top-5 left-5 font-mono-accent text-[10px] text-gold border border-gold/40 px-3 py-1.5 bg-obsidian/40 backdrop-blur-sm">
                      Step {s.n}
                    </div>
                  </div>
                </motion.div>

                <div className={`md:col-span-6 ${reverse ? "md:order-1 md:pr-4" : "md:pl-4"}`}>
                  <div className="font-display text-[8rem] md:text-[11rem] leading-[0.85] text-gold/15 -ml-2 -mb-4 select-none">
                    {s.n}
                  </div>
                  <h3 className="font-display text-4xl md:text-5xl text-bone leading-tight">
                    {s.title}
                  </h3>
                  <p className="mt-5 text-bone/70 text-lg leading-relaxed max-w-lg">
                    {s.body}
                  </p>
                  <div className="mt-6 flex items-center gap-3 font-mono-accent text-[10px] text-bone/40">
                    <span className="w-10 h-px bg-bone/20" />
                    <span>0{idx + 1} · of · 03</span>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
